/**
 * ============================================================================
 * ImageSelector.jsx v3.3 - BUTTON STRATEGY COMPLIANCE
 * ============================================================================
 * 
 * 🔧 v3.4: COMPACT + BUTTON STRATEGY (3 Fixes)
 *   FIX 1: Tab-Buttons → Chip-Toggle (36px, pill, overflow:hidden container)
 *   FIX 2: clearBtn → Chip Danger (36px, pill, #FEE2E2)
 *   FIX 3: showMoreBtn → L3 Ghost (40px, 10px, dashed→solid)
 *
 * ✅ v3.2: 82 Stock-Bilder (erweitert)
 * ✅ v3.1: Lokale Pfade statt Unsplash-URLs
 * ✅ v3.0: Stock-Bilder Tab hinzugefügt
 * 
 * @version 3.4.0 - Compact + Button Strategy
 */

import React, { useState } from 'react';

// ═══════════════════════════════════════════════════════════════
// PLANIVIO FARBEN
// ═══════════════════════════════════════════════════════════════

const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  warm: '#E8976C',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#FFFFFF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
};

// ✅ v3.4: BUTTON STRATEGY TOKENS (korrigiert auf Spec-Werte)
const BTN = {
  chipH: '36px', chipR: '100px', chipFs: '0.82rem',
  ghostH: '40px', ghostR: '10px', ghostPad: '0 16px', ghostFs: '0.85rem',
  fw: 600,
};

// ═══════════════════════════════════════════════════════════════
// 82 STOCK-BILDER
// ═══════════════════════════════════════════════════════════════

const STOCK_IMAGES = [
  // FRÜHSTÜCK (10)
  { id: 'stock-breakfast', label: 'Frühstück', url: '/images/recipes/breakfast.jpg', categories: ['Frühstück'] },
  { id: 'stock-pancakes', label: 'Pancakes', url: '/images/recipes/pancakes.jpg', categories: ['Frühstück', 'Dessert'] },
  { id: 'stock-porridge', label: 'Porridge', url: '/images/recipes/porridge.jpg', categories: ['Frühstück'] },
  { id: 'stock-avocado', label: 'Avocado Toast', url: '/images/recipes/avocado-toast.jpg', categories: ['Frühstück', 'Snack'] },
  { id: 'stock-waffles', label: 'Waffeln', url: '/images/recipes/waffles.jpg', categories: ['Frühstück', 'Dessert'] },
  { id: 'stock-croissant', label: 'Croissant', url: '/images/recipes/croissant.jpg', categories: ['Frühstück', 'Snack'] },
  { id: 'stock-eggs-benedict', label: 'Eggs Benedict', url: '/images/recipes/eggs-benedict.jpg', categories: ['Frühstück'] },
  { id: 'stock-muesli', label: 'Müsli', url: '/images/recipes/muesli.jpg', categories: ['Frühstück'] },
  { id: 'stock-french-toast', label: 'French Toast', url: '/images/recipes/french-toast.jpg', categories: ['Frühstück', 'Dessert'] },
  { id: 'stock-smoothie-bowl', label: 'Smoothie Bowl', url: '/images/recipes/smoothie-bowl.jpg', categories: ['Frühstück'] },
  // PASTA & ITALIENISCH (8)
  { id: 'stock-pasta', label: 'Pasta', url: '/images/recipes/pasta.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-spaghetti', label: 'Spaghetti', url: '/images/recipes/spaghetti.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-lasagna', label: 'Lasagne', url: '/images/recipes/lasagna.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-risotto', label: 'Risotto', url: '/images/recipes/risotto.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-gnocchi', label: 'Gnocchi', url: '/images/recipes/gnocchi.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-pizza', label: 'Pizza', url: '/images/recipes/pizza.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-bruschetta', label: 'Bruschetta', url: '/images/recipes/bruschetta.jpg', categories: ['Vorspeise', 'Snack'] },
  { id: 'stock-caprese', label: 'Caprese', url: '/images/recipes/caprese.jpg', categories: ['Vorspeise', 'Salat'] },
  // FLEISCH (10)
  { id: 'stock-burger', label: 'Burger', url: '/images/recipes/burger.jpg', categories: ['Hauptgericht', 'Snack'] },
  { id: 'stock-steak', label: 'Steak', url: '/images/recipes/steak.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-chicken', label: 'Hähnchen', url: '/images/recipes/chicken.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-schnitzel', label: 'Schnitzel', url: '/images/recipes/schnitzel.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-meatballs', label: 'Fleischbällchen', url: '/images/recipes/meatballs.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-ribs', label: 'Ribs', url: '/images/recipes/ribs.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-roast', label: 'Braten', url: '/images/recipes/roast.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-kebab', label: 'Kebab', url: '/images/recipes/kebab.jpg', categories: ['Hauptgericht', 'Snack'] },
  { id: 'stock-pulled-pork', label: 'Pulled Pork', url: '/images/recipes/pulled-pork.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-wings', label: 'Chicken Wings', url: '/images/recipes/wings.jpg', categories: ['Hauptgericht', 'Snack'] },
  // FISCH & MEERESFRÜCHTE (6)
  { id: 'stock-salmon', label: 'Lachs', url: '/images/recipes/salmon.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-shrimp', label: 'Garnelen', url: '/images/recipes/shrimp.jpg', categories: ['Hauptgericht', 'Vorspeise'] },
  { id: 'stock-fish-fillet', label: 'Fischfilet', url: '/images/recipes/fish-fillet.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-seafood', label: 'Meeresfrüchte', url: '/images/recipes/seafood.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-tuna', label: 'Thunfisch', url: '/images/recipes/tuna.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-fish-tacos', label: 'Fish Tacos', url: '/images/recipes/fish-tacos.jpg', categories: ['Hauptgericht', 'Snack'] },
  // ASIATISCH (10)
  { id: 'stock-sushi', label: 'Sushi', url: '/images/recipes/sushi.jpg', categories: ['Hauptgericht', 'Vorspeise'] },
  { id: 'stock-ramen', label: 'Ramen', url: '/images/recipes/ramen.jpg', categories: ['Hauptgericht', 'Suppe'] },
  { id: 'stock-pho', label: 'Pho', url: '/images/recipes/pho.jpg', categories: ['Hauptgericht', 'Suppe'] },
  { id: 'stock-curry', label: 'Curry', url: '/images/recipes/curry.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-rice-bowl', label: 'Reis-Bowl', url: '/images/recipes/rice-bowl.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-fried-rice', label: 'Gebratener Reis', url: '/images/recipes/fried-rice.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-pad-thai', label: 'Pad Thai', url: '/images/recipes/pad-thai.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-dim-sum', label: 'Dim Sum', url: '/images/recipes/dim-sum.jpg', categories: ['Vorspeise', 'Snack'] },
  { id: 'stock-spring-rolls', label: 'Frühlingsrollen', url: '/images/recipes/spring-rolls.jpg', categories: ['Vorspeise', 'Snack'] },
  { id: 'stock-bibimbap', label: 'Bibimbap', url: '/images/recipes/bibimbap.jpg', categories: ['Hauptgericht'] },
  // MEXIKANISCH (5)
  { id: 'stock-tacos', label: 'Tacos', url: '/images/recipes/tacos.jpg', categories: ['Hauptgericht', 'Snack'] },
  { id: 'stock-burrito', label: 'Burrito', url: '/images/recipes/burrito.jpg', categories: ['Hauptgericht'] },
  { id: 'stock-quesadilla', label: 'Quesadilla', url: '/images/recipes/quesadilla.jpg', categories: ['Hauptgericht', 'Snack'] },
  { id: 'stock-nachos', label: 'Nachos', url: '/images/recipes/nachos.jpg', categories: ['Snack'] },
  { id: 'stock-guacamole', label: 'Guacamole', url: '/images/recipes/guacamole.jpg', categories: ['Snack', 'Vorspeise'] },
  // SALATE & BOWLS (6)
  { id: 'stock-salad', label: 'Salat', url: '/images/recipes/salad.jpg', categories: ['Salat', 'Vorspeise'] },
  { id: 'stock-caesar', label: 'Caesar Salad', url: '/images/recipes/caesar-salad.jpg', categories: ['Salat'] },
  { id: 'stock-buddha-bowl', label: 'Buddha Bowl', url: '/images/recipes/buddha-bowl.jpg', categories: ['Hauptgericht', 'Salat'] },
  { id: 'stock-greek-salad', label: 'Griechischer Salat', url: '/images/recipes/greek-salad.jpg', categories: ['Salat', 'Vorspeise'] },
  { id: 'stock-poke-bowl', label: 'Poke Bowl', url: '/images/recipes/poke-bowl.jpg', categories: ['Hauptgericht', 'Salat'] },
  { id: 'stock-couscous', label: 'Couscous', url: '/images/recipes/couscous.jpg', categories: ['Salat', 'Beilage'] },
  // SUPPEN (6)
  { id: 'stock-soup', label: 'Suppe', url: '/images/recipes/soup.jpg', categories: ['Suppe'] },
  { id: 'stock-tomato-soup', label: 'Tomatensuppe', url: '/images/recipes/tomato-soup.jpg', categories: ['Suppe'] },
  { id: 'stock-pumpkin-soup', label: 'Kürbissuppe', url: '/images/recipes/pumpkin-soup.jpg', categories: ['Suppe'] },
  { id: 'stock-chicken-soup', label: 'Hühnersuppe', url: '/images/recipes/chicken-soup.jpg', categories: ['Suppe'] },
  { id: 'stock-lentil-soup', label: 'Linsensuppe', url: '/images/recipes/lentil-soup.jpg', categories: ['Suppe'] },
  { id: 'stock-minestrone', label: 'Minestrone', url: '/images/recipes/minestrone.jpg', categories: ['Suppe'] },
  // SNACKS (6)
  { id: 'stock-sandwich', label: 'Sandwich', url: '/images/recipes/sandwich.jpg', categories: ['Snack', 'Frühstück'] },
  { id: 'stock-wrap', label: 'Wrap', url: '/images/recipes/wrap.jpg', categories: ['Snack', 'Hauptgericht'] },
  { id: 'stock-hummus', label: 'Hummus', url: '/images/recipes/hummus.jpg', categories: ['Snack', 'Vorspeise'] },
  { id: 'stock-bruschetta-snack', label: 'Bruschetta', url: '/images/recipes/bruschetta-snack.jpg', categories: ['Snack', 'Vorspeise'] },
  { id: 'stock-cheese-plate', label: 'Käseplatte', url: '/images/recipes/cheese-plate.jpg', categories: ['Snack', 'Vorspeise'] },
  { id: 'stock-antipasti', label: 'Antipasti', url: '/images/recipes/antipasti.jpg', categories: ['Snack', 'Vorspeise'] },
  // BEILAGEN & VEGETARISCH (6)
  { id: 'stock-veggie', label: 'Gemüse', url: '/images/recipes/veggie.jpg', categories: ['Beilage', 'Hauptgericht'] },
  { id: 'stock-roasted-veggies', label: 'Ofengemüse', url: '/images/recipes/roasted-veggies.jpg', categories: ['Beilage'] },
  { id: 'stock-potatoes', label: 'Kartoffeln', url: '/images/recipes/potatoes.jpg', categories: ['Beilage'] },
  { id: 'stock-fries', label: 'Pommes', url: '/images/recipes/fries.jpg', categories: ['Beilage', 'Snack'] },
  { id: 'stock-mashed-potatoes', label: 'Kartoffelpüree', url: '/images/recipes/mashed-potatoes.jpg', categories: ['Beilage'] },
  { id: 'stock-grilled-veggies', label: 'Grill-Gemüse', url: '/images/recipes/grilled-veggies.jpg', categories: ['Beilage'] },
  // DESSERTS (10)
  { id: 'stock-cake', label: 'Kuchen', url: '/images/recipes/cake.jpg', categories: ['Dessert'] },
  { id: 'stock-cheesecake', label: 'Cheesecake', url: '/images/recipes/cheesecake.jpg', categories: ['Dessert'] },
  { id: 'stock-icecream', label: 'Eis', url: '/images/recipes/icecream.jpg', categories: ['Dessert'] },
  { id: 'stock-cookies', label: 'Kekse', url: '/images/recipes/cookies.jpg', categories: ['Dessert', 'Snack'] },
  { id: 'stock-brownies', label: 'Brownies', url: '/images/recipes/brownies.jpg', categories: ['Dessert'] },
  { id: 'stock-tiramisu', label: 'Tiramisu', url: '/images/recipes/tiramisu.jpg', categories: ['Dessert'] },
  { id: 'stock-panna-cotta', label: 'Panna Cotta', url: '/images/recipes/panna-cotta.jpg', categories: ['Dessert'] },
  { id: 'stock-apple-pie', label: 'Apfelkuchen', url: '/images/recipes/apple-pie.jpg', categories: ['Dessert'] },
  { id: 'stock-chocolate-mousse', label: 'Mousse au Chocolat', url: '/images/recipes/chocolate-mousse.jpg', categories: ['Dessert'] },
  { id: 'stock-fruit-salad', label: 'Obstsalat', url: '/images/recipes/fruit-salad.jpg', categories: ['Dessert', 'Frühstück'] },
  // BACKWAREN (4)
  { id: 'stock-bread', label: 'Brot', url: '/images/recipes/bread.jpg', categories: ['Frühstück', 'Beilage'] },
  { id: 'stock-baguette', label: 'Baguette', url: '/images/recipes/baguette.jpg', categories: ['Frühstück', 'Beilage'] },
  { id: 'stock-rolls', label: 'Brötchen', url: '/images/recipes/rolls.jpg', categories: ['Frühstück'] },
  { id: 'stock-pretzel', label: 'Brezel', url: '/images/recipes/pretzel.jpg', categories: ['Snack'] },
  // GETRÄNKE (5)
  { id: 'stock-smoothie', label: 'Smoothie', url: '/images/recipes/smoothie.jpg', categories: ['Getränk', 'Frühstück'] },
  { id: 'stock-coffee', label: 'Kaffee', url: '/images/recipes/coffee.jpg', categories: ['Getränk', 'Frühstück'] },
  { id: 'stock-juice', label: 'Saft', url: '/images/recipes/juice.jpg', categories: ['Getränk', 'Frühstück'] },
  { id: 'stock-lemonade', label: 'Limonade', url: '/images/recipes/lemonade.jpg', categories: ['Getränk'] },
  { id: 'stock-hot-chocolate', label: 'Heiße Schokolade', url: '/images/recipes/hot-chocolate.jpg', categories: ['Getränk'] },
];

// ═══════════════════════════════════════════════════════════════
// 65 EMOJI TEMPLATE-BILDER
// ═══════════════════════════════════════════════════════════════

const TEMPLATE_IMAGES = [
  { id: 'pasta', label: 'Pasta', emoji: '🍝', categories: ['Hauptgericht'] },
  { id: 'pizza', label: 'Pizza', emoji: '🍕', categories: ['Hauptgericht'] },
  { id: 'burger', label: 'Burger', emoji: '🍔', categories: ['Hauptgericht', 'Snack'] },
  { id: 'steak', label: 'Steak', emoji: '🥩', categories: ['Hauptgericht'] },
  { id: 'chicken', label: 'Hähnchen', emoji: '🍗', categories: ['Hauptgericht'] },
  { id: 'ribs', label: 'Ribs', emoji: '🍖', categories: ['Hauptgericht'] },
  { id: 'fish', label: 'Fisch', emoji: '🐟', categories: ['Hauptgericht'] },
  { id: 'shrimp', label: 'Garnelen', emoji: '🍤', categories: ['Hauptgericht', 'Vorspeise'] },
  { id: 'stew', label: 'Eintopf', emoji: '🥘', categories: ['Hauptgericht', 'Suppe'] },
  { id: 'curry', label: 'Curry', emoji: '🍛', categories: ['Hauptgericht'] },
  { id: 'rice', label: 'Reis', emoji: '🍚', categories: ['Hauptgericht', 'Beilage'] },
  { id: 'asian', label: 'Asiatisch', emoji: '🍜', categories: ['Hauptgericht'] },
  { id: 'sushi', label: 'Sushi', emoji: '🍣', categories: ['Hauptgericht', 'Vorspeise'] },
  { id: 'bento', label: 'Bento', emoji: '🍱', categories: ['Hauptgericht'] },
  { id: 'dumpling', label: 'Dumpling', emoji: '🥟', categories: ['Hauptgericht', 'Vorspeise'] },
  { id: 'taco', label: 'Mexikanisch', emoji: '🌮', categories: ['Hauptgericht'] },
  { id: 'burrito', label: 'Burrito', emoji: '🌯', categories: ['Hauptgericht'] },
  { id: 'falafel', label: 'Falafel', emoji: '🧆', categories: ['Hauptgericht', 'Snack'] },
  { id: 'wrap', label: 'Wrap', emoji: '🥙', categories: ['Hauptgericht', 'Snack'] },
  { id: 'soup', label: 'Suppe', emoji: '🍲', categories: ['Suppe', 'Vorspeise'] },
  { id: 'salad', label: 'Salat', emoji: '🥗', categories: ['Salat', 'Vorspeise', 'Beilage'] },
  { id: 'greens', label: 'Grün', emoji: '🥬', categories: ['Salat', 'Beilage'] },
  { id: 'sandwich', label: 'Sandwich', emoji: '🥪', categories: ['Snack', 'Frühstück'] },
  { id: 'hotdog', label: 'Hotdog', emoji: '🌭', categories: ['Snack'] },
  { id: 'fries', label: 'Pommes', emoji: '🍟', categories: ['Snack', 'Beilage'] },
  { id: 'bacon', label: 'Bacon', emoji: '🥓', categories: ['Snack', 'Frühstück'] },
  { id: 'pretzel', label: 'Brezel', emoji: '🥨', categories: ['Snack'] },
  { id: 'popcorn', label: 'Popcorn', emoji: '🍿', categories: ['Snack'] },
  { id: 'breakfast', label: 'Frühstück', emoji: '🍳', categories: ['Frühstück'] },
  { id: 'egg', label: 'Ei', emoji: '🥚', categories: ['Frühstück', 'Beilage'] },
  { id: 'pancakes', label: 'Pancakes', emoji: '🥞', categories: ['Frühstück', 'Dessert'] },
  { id: 'waffle', label: 'Waffel', emoji: '🧇', categories: ['Frühstück', 'Dessert'] },
  { id: 'croissant', label: 'Croissant', emoji: '🥐', categories: ['Frühstück', 'Snack'] },
  { id: 'bread', label: 'Brot', emoji: '🍞', categories: ['Frühstück', 'Beilage'] },
  { id: 'bagel', label: 'Bagel', emoji: '🥯', categories: ['Frühstück', 'Snack'] },
  { id: 'dessert', label: 'Dessert', emoji: '🍰', categories: ['Dessert'] },
  { id: 'cake', label: 'Kuchen', emoji: '🎂', categories: ['Dessert'] },
  { id: 'cupcake', label: 'Muffin', emoji: '🧁', categories: ['Dessert', 'Snack'] },
  { id: 'pie', label: 'Pie', emoji: '🥧', categories: ['Dessert'] },
  { id: 'donut', label: 'Donut', emoji: '🍩', categories: ['Dessert', 'Snack'] },
  { id: 'cookie', label: 'Kekse', emoji: '🍪', categories: ['Dessert', 'Snack'] },
  { id: 'chocolate', label: 'Schokolade', emoji: '🍫', categories: ['Dessert', 'Snack'] },
  { id: 'icecream', label: 'Eis', emoji: '🍨', categories: ['Dessert'] },
  { id: 'pudding', label: 'Pudding', emoji: '🍮', categories: ['Dessert'] },
  { id: 'honey', label: 'Honig', emoji: '🍯', categories: ['Dessert', 'Frühstück'] },
  { id: 'veggie', label: 'Vegetarisch', emoji: '🥦', categories: ['Hauptgericht', 'Beilage'] },
  { id: 'carrot', label: 'Karotten', emoji: '🥕', categories: ['Beilage'] },
  { id: 'corn', label: 'Mais', emoji: '🌽', categories: ['Beilage'] },
  { id: 'potato', label: 'Kartoffel', emoji: '🥔', categories: ['Beilage', 'Hauptgericht'] },
  { id: 'eggplant', label: 'Aubergine', emoji: '🍆', categories: ['Hauptgericht', 'Beilage'] },
  { id: 'tomato', label: 'Tomate', emoji: '🍅', categories: ['Beilage', 'Salat'] },
  { id: 'mushroom', label: 'Pilze', emoji: '🍄', categories: ['Beilage', 'Hauptgericht'] },
  { id: 'fruit', label: 'Obst', emoji: '🍇', categories: ['Dessert', 'Snack'] },
  { id: 'apple', label: 'Apfel', emoji: '🍎', categories: ['Dessert', 'Snack'] },
  { id: 'strawberry', label: 'Erdbeere', emoji: '🍓', categories: ['Dessert'] },
  { id: 'banana', label: 'Banane', emoji: '🍌', categories: ['Dessert', 'Snack', 'Frühstück'] },
  { id: 'avocado', label: 'Avocado', emoji: '🥑', categories: ['Salat', 'Frühstück', 'Snack'] },
  { id: 'drink', label: 'Getränk', emoji: '🥤', categories: ['Getränk'] },
  { id: 'coffee', label: 'Kaffee', emoji: '☕', categories: ['Getränk', 'Frühstück'] },
  { id: 'tea', label: 'Tee', emoji: '🍵', categories: ['Getränk'] },
  { id: 'juice', label: 'Saft', emoji: '🧃', categories: ['Getränk', 'Frühstück'] },
  { id: 'cocktail', label: 'Cocktail', emoji: '🍹', categories: ['Getränk'] },
  { id: 'wine', label: 'Wein', emoji: '🍷', categories: ['Getränk'] },
  { id: 'beer', label: 'Bier', emoji: '🍺', categories: ['Getränk'] },
  { id: 'cheese', label: 'Käse', emoji: '🧀', categories: ['Snack', 'Beilage'] },
  { id: 'nuts', label: 'Nüsse', emoji: '🥜', categories: ['Snack'] },
  { id: 'default', label: 'Standard', emoji: '🍽️', categories: [] },
];

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const ImageSelector = ({ 
  value, 
  onChange, 
  category = null,
  disabled = false 
}) => {
  const [activeTab, setActiveTab] = useState('stock');
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  const getSortedImages = (images) => {
    let filtered = [...images];
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(img => 
        img.label.toLowerCase().includes(search) || img.id.toLowerCase().includes(search)
      );
    }
    if (category && !searchTerm.trim()) {
      const matching = filtered.filter(img => img.categories.includes(category));
      const others = filtered.filter(img => !img.categories.includes(category));
      return [...matching, ...others];
    }
    return filtered;
  };

  const sortedStockImages = getSortedImages(STOCK_IMAGES);
  const sortedEmojiImages = getSortedImages(TEMPLATE_IMAGES);
  const displayStock = showAll ? sortedStockImages : sortedStockImages.slice(0, 12);
  const displayEmoji = showAll ? sortedEmojiImages : sortedEmojiImages.slice(0, 12);

  const handleSelect = (imageValue) => {
    if (disabled) return;
    onChange?.(imageValue);
  };

  const handleImageError = (id) => {
    setImageLoadErrors(prev => ({ ...prev, [id]: true }));
  };

  const isCurrentValueUrl = value?.startsWith('http') || value?.startsWith('/');
  const currentStockImage = STOCK_IMAGES.find(img => img.url === value);
  const currentEmojiImage = TEMPLATE_IMAGES.find(img => img.id === value);

  // ============================================
  // ✅ v3.3: BUTTON STRATEGY STYLES
  // ============================================
  const styles = {
    container: { width: '100%' },
    
    // 🔧 FIX 1: Tab-Container → Chip-Toggle-Gruppe (pill, overflow:hidden)
    tabContainer: {
      display: 'flex',
      gap: 0,  // Keine Lücke — durchgehende Pill-Form
      marginBottom: '0.75rem',
      background: PLANIVIO.bgLight,
      padding: '3px',
      borderRadius: BTN.chipR,  // pill!
      overflow: 'hidden',
      border: `1px solid ${PLANIVIO.borderLight}`,
    },
    // 🔧 FIX 1: Tab → Chip (36px, pill, keine innere Rundung)
    tab: {
      flex: 1,
      height: BTN.chipH,
      padding: '0 12px',
      border: 'none',
      borderRadius: BTN.chipR,  // pill — innere Tabs auch pill
      cursor: 'pointer',
      fontSize: BTN.chipFs,
      fontWeight: 500,
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.4rem',
      lineHeight: 1,
      boxSizing: 'border-box',
    },
    tabActive: { 
      background: 'white', 
      color: PLANIVIO.teal, 
      fontWeight: BTN.fw,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
    },
    tabInactive: { 
      background: 'transparent', 
      color: PLANIVIO.textMuted 
    },
    
    searchContainer: { marginBottom: '0.75rem' },
    searchWrapper: { position: 'relative' },
    searchInput: {
      width: '100%',
      padding: '0.6rem 1rem 0.6rem 2.5rem',
      border: '2px solid ' + PLANIVIO.borderLight,
      borderRadius: '10px',
      fontSize: '0.9rem',
      outline: 'none',
      boxSizing: 'border-box',
    },
    searchIcon: {
      position: 'absolute', left: '0.85rem', top: '50%',
      transform: 'translateY(-50%)', fontSize: '1rem', opacity: 0.5,
    },
    grid: {
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '0.5rem', marginBottom: '0.75rem',
    },
    stockCard: {
      position: 'relative', aspectRatio: '4/3', borderRadius: '10px',
      border: '2px solid ' + PLANIVIO.borderLight,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease', overflow: 'hidden',
      opacity: disabled ? 0.5 : 1,
    },
    stockImage: { width: '100%', height: '100%', objectFit: 'cover' },
    stockLabel: {
      position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0.4rem',
      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
      color: 'white', fontSize: '0.65rem', fontWeight: 600, textAlign: 'center',
    },
    emojiCard: {
      position: 'relative', aspectRatio: '1', borderRadius: '10px',
      border: '2px solid ' + PLANIVIO.borderLight, background: PLANIVIO.bgLight,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', opacity: disabled ? 0.5 : 1, padding: '0.25rem',
    },
    emoji: { fontSize: '1.5rem', marginBottom: '0.1rem' },
    emojiLabel: { fontSize: '0.6rem', fontWeight: 600, color: PLANIVIO.text, textAlign: 'center', lineHeight: 1.1 },
    cardSelected: { border: '3px solid ' + PLANIVIO.teal, boxShadow: '0 0 0 3px ' + PLANIVIO.teal + '20' },
    checkmark: {
      position: 'absolute', top: '4px', right: '4px',
      width: '20px', height: '20px', borderRadius: '50%',
      background: PLANIVIO.gradient,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontSize: '0.7rem', fontWeight: 'bold',
    },
    selectedPreview: {
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.6rem 1rem',
      background: 'linear-gradient(135deg, ' + PLANIVIO.bgLight + ' 0%, white 100%)',
      border: '2px solid ' + PLANIVIO.teal, borderRadius: '12px',
      marginBottom: '0.75rem',
    },
    previewImage: { width: '60px', height: '45px', borderRadius: '8px', objectFit: 'cover' },
    previewEmoji: { fontSize: '2.5rem' },
    previewInfo: { flex: 1 },
    previewLabel: { fontWeight: 700, color: PLANIVIO.text, fontSize: '0.95rem' },
    previewHint: { fontSize: '0.7rem', color: PLANIVIO.textMuted },
    
    // 🔧 FIX 2: clearBtn → Chip Danger (36px, pill)
    clearBtn: {
      height: BTN.chipH,
      padding: '0 14px',
      border: '1px solid #FECACA',
      borderRadius: BTN.chipR,
      background: '#FEE2E2',
      color: '#DC2626',
      fontSize: BTN.chipFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      lineHeight: 1,
      boxSizing: 'border-box',
      whiteSpace: 'nowrap',
      transition: 'all 0.15s ease',
      flexShrink: 0,
    },
    
    // 🔧 FIX 3: showMoreBtn → L3 Ghost (40px, 10px)
    showMoreBtn: {
      width: '100%',
      height: BTN.ghostH,
      padding: BTN.ghostPad,
      border: `1px solid ${PLANIVIO.border}`,
      borderRadius: BTN.ghostR,
      background: 'transparent',
      color: PLANIVIO.teal,
      fontSize: BTN.ghostFs,
      fontWeight: BTN.fw,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box',
      transition: 'all 0.2s ease',
    },
    
    categoryHint: {
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      padding: '0.4rem 0.75rem', background: PLANIVIO.bgLight,
      borderRadius: '8px', marginBottom: '0.75rem',
      fontSize: '0.75rem', color: PLANIVIO.teal,
    },
    stats: { fontSize: '0.7rem', color: PLANIVIO.textMuted, textAlign: 'center', marginTop: '0.5rem' },
  };

  return (
    <div style={styles.container}>
      
      {/* Selected Preview */}
      {value && (
        <div style={styles.selectedPreview}>
          {isCurrentValueUrl ? (
            <img src={value} alt="Ausgewählt" style={styles.previewImage} />
          ) : (
            <span style={styles.previewEmoji}>{currentEmojiImage?.emoji || '🍽️'}</span>
          )}
          <div style={styles.previewInfo}>
            <div style={styles.previewLabel}>
              {currentStockImage?.label || currentEmojiImage?.label || 'Ausgewählt'}
            </div>
            <div style={styles.previewHint}>
              {isCurrentValueUrl ? '📷 Stock-Bild' : '😊 Emoji'}
            </div>
          </div>
          {/* 🔧 FIX 2: clearBtn → Chip Danger */}
          <button 
            style={styles.clearBtn} 
            onClick={() => handleSelect('')} 
            disabled={disabled}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FCA5A5'; e.currentTarget.style.color = '#991B1B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.color = '#DC2626'; }}
          >
            ✕ Entfernen
          </button>
        </div>
      )}

      {/* 🔧 FIX 1: Tabs → Chip-Toggle-Gruppe (36px, pill) */}
      <div style={styles.tabContainer}>
        <button 
          style={{ ...styles.tab, ...(activeTab === 'stock' ? styles.tabActive : styles.tabInactive) }}
          onClick={() => { setActiveTab('stock'); setShowAll(false); setSearchTerm(''); }}
        >
          📷 Stock-Bilder ({STOCK_IMAGES.length})
        </button>
        <button 
          style={{ ...styles.tab, ...(activeTab === 'emoji' ? styles.tabActive : styles.tabInactive) }}
          onClick={() => { setActiveTab('emoji'); setShowAll(false); setSearchTerm(''); }}
        >
          😊 Emojis ({TEMPLATE_IMAGES.length})
        </button>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder={activeTab === 'stock' ? 'Stock-Bilder suchen...' : 'Emojis suchen...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* Category Hint */}
      {category && !value && !searchTerm && (
        <div style={styles.categoryHint}>
          <span>💡</span>
          <span>Vorschläge für <strong>{category}</strong> zuerst</span>
        </div>
      )}

      {/* Stock Tab */}
      {activeTab === 'stock' && (
        <>
          <div style={styles.grid}>
            {displayStock.map((img) => {
              const isSelected = value === img.url;
              const hasError = imageLoadErrors[img.id];
              if (hasError) return null;
              
              return (
                <div
                  key={img.id}
                  style={{ ...styles.stockCard, ...(isSelected ? styles.cardSelected : {}) }}
                  onClick={() => handleSelect(img.url)}
                  onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.borderColor = PLANIVIO.teal; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = isSelected ? PLANIVIO.teal : PLANIVIO.borderLight; }}
                >
                  {isSelected && <div style={styles.checkmark}>✓</div>}
                  <img src={img.url} alt={img.label} style={styles.stockImage} onError={() => handleImageError(img.id)} loading="lazy" />
                  <div style={styles.stockLabel}>{img.label}</div>
                </div>
              );
            })}
          </div>
          
          {displayStock.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: PLANIVIO.textMuted }}>
              <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>🔍</span>
              Keine Bilder gefunden
            </div>
          )}
          
          {/* 🔧 FIX 3: showMoreBtn → L3 Ghost */}
          {!searchTerm && sortedStockImages.length > 12 && !showAll && (
            <button 
              style={styles.showMoreBtn} 
              onClick={() => setShowAll(true)}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              + {sortedStockImages.length - 12} weitere Bilder
            </button>
          )}
          
          {showAll && sortedStockImages.length > 12 && (
            <button 
              style={styles.showMoreBtn} 
              onClick={() => setShowAll(false)}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Weniger anzeigen
            </button>
          )}
          
          <div style={styles.stats}>{sortedStockImages.length} Stock-Bilder verfügbar</div>
        </>
      )}

      {/* Emoji Tab */}
      {activeTab === 'emoji' && (
        <>
          <div style={{ ...styles.grid, gridTemplateColumns: 'repeat(6, 1fr)' }}>
            {displayEmoji.map((img) => {
              const isSelected = value === img.id;
              
              return (
                <div
                  key={img.id}
                  style={{ ...styles.emojiCard, ...(isSelected ? styles.cardSelected : {}) }}
                  onClick={() => handleSelect(img.id)}
                  onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.borderColor = PLANIVIO.teal; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = isSelected ? PLANIVIO.teal : PLANIVIO.borderLight; }}
                >
                  {isSelected && <div style={styles.checkmark}>✓</div>}
                  <span style={styles.emoji}>{img.emoji}</span>
                  <span style={styles.emojiLabel}>{img.label}</span>
                </div>
              );
            })}
          </div>
          
          {displayEmoji.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: PLANIVIO.textMuted }}>
              <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}>🔍</span>
              Keine Emojis gefunden
            </div>
          )}
          
          {/* 🔧 FIX 3: showMoreBtn → L3 Ghost */}
          {!searchTerm && sortedEmojiImages.length > 12 && !showAll && (
            <button 
              style={styles.showMoreBtn} 
              onClick={() => setShowAll(true)}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              + {sortedEmojiImages.length - 12} weitere Emojis
            </button>
          )}
          
          {showAll && sortedEmojiImages.length > 12 && (
            <button 
              style={styles.showMoreBtn} 
              onClick={() => setShowAll(false)}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,144,164,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Weniger anzeigen
            </button>
          )}
          
          <div style={styles.stats}>{sortedEmojiImages.length} Emojis verfügbar</div>
        </>
      )}
    </div>
  );
};

export default ImageSelector;

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

export const getRecipeEmoji = (imageKey) => {
  if (!imageKey) return '🍽️';
  if (imageKey.startsWith('http') || imageKey.startsWith('/')) return '🍽️';
  const image = TEMPLATE_IMAGES.find(img => img.id === imageKey);
  return image?.emoji || '🍽️';
};

export const getRecipeImageLabel = (imageKey) => {
  const image = TEMPLATE_IMAGES.find(img => img.id === imageKey);
  return image?.label || 'Rezept';
};

export const isImageUrl = (value) => {
  return value?.startsWith('http') || value?.startsWith('/');
};

export { TEMPLATE_IMAGES, STOCK_IMAGES };